import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getSupabase } from "@/lib/hooks/api/utils";
import { getSafeErrorMessage } from "@/lib/utils/safe-error";
import type { Contacts, ContactsInsertInput, ContactsUpdateInput } from "@/src/gql/graphql";
import { ContactsInsertInputSchema, ContactsUpdateInputSchema } from "@/src/gql/validation";
import { validateContactUpdatePayload, validateContactField } from "@/lib/hooks/api/validation/contact-field-validator";
import type { UpdateContactContext } from "@/features/contacts/types/contact-api";
import type { CreateClientForm } from "@/features/contacts/types";

export function useUpdateContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: ContactsUpdateInput }) => {
      const validatedData = ContactsUpdateInputSchema.parse(updates);

      const payloadValidation = validateContactUpdatePayload(validatedData);
      if (!payloadValidation.isValid) {
        const errorMessage = payloadValidation.errors.join("; ");
        console.error("Contact update validation failed:", errorMessage);
        throw new Error(`Validation failed: ${errorMessage}`);
      }

      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("contacts")
        .update({ ...payloadValidation.sanitized, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.error("Error updating contact:", error);
        throw new Error(error.message);
      }

      return data;
    },
    onMutate: async ({ id, updates }): Promise<UpdateContactContext> => {
      await queryClient.cancelQueries({ queryKey: ["contacts"] });
      await queryClient.cancelQueries({ queryKey: ["contacts", id] });
      await queryClient.cancelQueries({ queryKey: ["contacts-paginated"] });

      const previousContacts = queryClient.getQueryData<Contacts[]>(["contacts"]);
      const previousContact = queryClient.getQueryData<Contacts>(["contacts", id]);
      const previousPaginated = queryClient.getQueryData<{ data: Contacts[] }>(["contacts-paginated"]);

      queryClient.setQueryData(["contacts", id], (old: Contacts | undefined) => {
        if (!old) return old;
        return { ...old, ...updates };
      });

      queryClient.setQueryData(["contacts"], (old: Contacts[] | undefined) => {
        if (!old) return old;
        return old.map((contact) => (contact.id === id ? { ...contact, ...updates } : contact));
      });

      queryClient.setQueryData(["contacts-paginated"], (old: { data: Contacts[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          data: old.data?.map((contact: Contacts) =>
            contact.id === id ? { ...contact, ...updates } : contact
          ),
        };
      });

      return { previousContacts, previousContact, previousPaginated };
    },
    onSuccess: (data: Contacts) => {
      queryClient.setQueryData(["contacts", data.id], data);

      queryClient.setQueryData(["contacts"], (old: Contacts[] | undefined) => {
        if (!old) return old;
        return old.map((contact) => (contact.id === data.id ? data : contact));
      });

      queryClient.setQueryData(["contacts-paginated"], (old: { data: Contacts[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          data: old.data?.map((contact: Contacts) => (contact.id === data.id ? data : contact)),
        };
      });

      if (data?.tags) {
        void queryClient.invalidateQueries({ queryKey: ["tags", "contacts"] });
        fetch("/api/tags/invalidate/contacts", { method: "POST" }).catch((error) => {
          console.error("Failed to invalidate server-side contact tags cache:", error);
        });
      }

      toast.success("Contact updated successfully");
    },
    onError: (error, _variables, onMutateResult: UpdateContactContext | undefined) => {
      if (onMutateResult?.previousContacts) {
        queryClient.setQueryData(["contacts"], onMutateResult.previousContacts);
      }
      if (onMutateResult?.previousContact) {
        queryClient.setQueryData(["contacts", _variables.id], onMutateResult.previousContact);
      }
      if (onMutateResult?.previousPaginated) {
        queryClient.setQueryData(["contacts-paginated"], onMutateResult.previousPaginated);
      }

      const errorMessage = getSafeErrorMessage(error);
      toast.error(`Update failed: ${errorMessage}`);
      console.error("Error updating contact:", error);
    },
  });
}

export function useDeleteContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const supabase = getSupabase();
      const { error } = await supabase.from("contacts").delete().eq("id", id);

      if (error) {
        console.error("Error deleting contact:", error);
        throw new Error(error.message);
      }

      return { id, deleted: true };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["contacts"] });
      void queryClient.invalidateQueries({ queryKey: ["tags", "contacts"] });
      fetch("/api/tags/invalidate/contacts", { method: "POST" }).catch((error) => {
        console.error("Failed to invalidate server-side contact tags cache:", error);
      });
      toast.success("Contact deleted successfully");
    },
    onError: (error) => {
      toast.error("Failed to delete contact");
      console.error("Error deleting contact:", error);
    },
  });
}

export function useCreateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (clientData: CreateClientForm) => {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("contacts")
        .insert([{ ...clientData, status: "customer" }])
        .select()
        .single();

      if (error) {
        console.error("Error creating client:", error);
        throw new Error(error.message);
      }

      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
    onError: (error) => {
      toast.error("Failed to create client");
      console.error("Error creating client:", error);
    },
  });
}

export function useCreateContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contactData: ContactsInsertInput) => {
      const validatedData = ContactsInsertInputSchema.parse(contactData);

      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("contacts")
        .insert([validatedData])
        .select()
        .single();

      if (error) {
        console.error("Error creating contact:", error);
        throw new Error(error.message);
      }

      return data;
    },
    retry: 0,
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["contacts"] });

      if (data?.tags) {
        void queryClient.invalidateQueries({ queryKey: ["tags", "contacts"] });
        fetch("/api/tags/invalidate/contacts", { method: "POST" }).catch((error) => {
          console.error("Failed to invalidate server-side contact tags cache:", error);
        });
      }
    },
    onError: (error) => {
      const errorMessage = getSafeErrorMessage(error);
      toast.error(`Creation failed: ${errorMessage}`);
      console.error("Error creating contact:", error);
    },
  });
}
